import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CardData from '../../../../../../utils/CardData';
import { WIDGET_TYPE_VIDEO } from '../../../../../../../../constants/scenarioConstant';
import { getValidWidgetPosition } from '../../../../../../utils/helper';

import Duration from './generalSettingsChild/Duration';
import { InteractivityProps } from './Interactivity';
import Position from './generalSettingsChild/Position';

interface LayoutProps extends InteractivityProps {
    // Define any additional props that the Layout component might need
}

const Layout = ({
    widgetTemplate,
	widgetStartTime,
	widgetEndTime,
	widgetOverlayRef,
    handleTimeAttributeChange,
    handleSubmitTimeAttribute,
    handleLocationAttributeChange,
}: LayoutProps) => {
    const { t: translator } = useTranslation();

    const {
        type,
        style: {
            coordinate: { x, y } = { x: 0, y: 0 },
            dimension: { w, h } = { w: 0, h: 0 },
            rotation = 0,
        },
    } = widgetTemplate;

    //ref to the selected input
    const selectedInputRef = useRef<null | HTMLDivElement>(null);
    // initMousedownPos to check if user click or drag
    const initMousedownPos = useRef<number>(0);
    // state of currentMouse position
    const [currentMousePos, setCurrentMousePos] = useState<number>(0);
    //property from child component x,y,w,h,roation...
    const selectedPropertyRef = useRef<null | string>(null);
    //old mouse position to check the direction of mouse
    const oldMouseYPos = useRef<number | undefined>();
    // callback to send updated value to redux
    const updateCallbackRef = useRef<null | Function>(null);

	const handleMouseActionSelectInput = (
		selectedInput: HTMLDivElement,
		event: React.MouseEvent<HTMLDivElement, MouseEvent>,
		styleProperty: string,
		updateCallback: (_updateValue: number) => void
	) => {
		// set selected input
		selectedInputRef.current = selectedInput;
		// init old position of mouse
		oldMouseYPos.current = event.pageX;
		// init initMousedownPos to check if user click or drag
		initMousedownPos.current = event.pageX;
		// callback to send updated value to redux
		updateCallbackRef.current = updateCallback;
		//property from child component, since closure prevent update value inside child
		selectedPropertyRef.current = styleProperty;
		//hidden bouding box in widget
		widgetOverlayRef.current.hiddenHighlightBox(false);
		setCurrentMousePos(event.pageX);
	};

    const handleMouseMoveEvent = (event: any) => {
        if (!selectedInputRef || !selectedInputRef.current || !oldMouseYPos.current) {
            return;
        }
        oldMouseYPos.current = currentMousePos;
        setCurrentMousePos(event.pageX);
    };

    const handleSetMouseUp = () => {
        if (!oldMouseYPos.current || !initMousedownPos.current) {
            return;
        }

        if (Math.abs(oldMouseYPos.current - initMousedownPos.current) === 0) {
            selectedInputRef.current?.click();
        }
        widgetOverlayRef.current.hiddenHighlightBox(true);
        selectedInputRef.current = null;
    };

	useEffect(() => {
		if (!oldMouseYPos.current || !updateCallbackRef.current || !selectedPropertyRef.current) {
			return;
		}

		if (Math.abs(oldMouseYPos.current - currentMousePos) < 0.1) {
			return;
		}
		let updatedValue;
		updatedValue = currentMousePos >= oldMouseYPos.current ? 10 : -10;

		switch (selectedPropertyRef.current) {
			case 'x':
				updatedValue = getValidWidgetPosition(updatedValue + x, 0);
				break;
			case 'y':
				updatedValue = getValidWidgetPosition(updatedValue + y, 0);
				break;
			case 'w':
				updatedValue = getValidWidgetPosition(updatedValue + w, 0);
				break;
			case 'h':
				updatedValue = getValidWidgetPosition(updatedValue + h, 0);
				break;
			case 'rotation':
				updatedValue = getValidWidgetPosition(updatedValue + rotation, 0, 360);
				break;
			default:
				updatedValue = 0;
		}
		updateCallbackRef.current(updatedValue);
	}, [currentMousePos]);

    return (
		<CardData title={translator('Layout')}>
			<Duration
				disabled={type === WIDGET_TYPE_VIDEO}
				widgetStartTime={widgetStartTime}
				widgetEndTime={widgetEndTime}
				handleTimeAttributeChange={handleTimeAttributeChange}
				handleSubmitTimeAttribute={handleSubmitTimeAttribute}
			/>

			{type !== WIDGET_TYPE_VIDEO && (
				<Position
					x={x}
					y={y}
					w={w}
					h={h}
					rotation={rotation}
					type={type}
					handleLocationAttributeChange={handleLocationAttributeChange}
					handleMouseActionSelectInput={handleMouseActionSelectInput}
					handleOnMouseMove={handleMouseMoveEvent}
					handleOnMouseUp={handleSetMouseUp}
				/>
			)}
        </CardData>
    );
};

export default Layout;
